<template>
  <Modal
    :handler="handler"
    :useActions="true"
    :width="60"
    :overflowOff="activeTab === 'general'"
    :closeOnBackdrop="false"
    @request-close="$emit('request-close')"
  >
    <template #head>
      <h3>{{ isEdit ? 'Editar Contato' : 'Adicionar Contato' }}</h3>
    </template>

    <template #content>
      <form class="columns contact-modal-form" @submit.prevent="submit">
        <div class="span-12 contact-modal-tabs tabs">
          <ul class="tab-list flex">
            <li class="tab-list-item">
              <a
                href="#"
                :class="{ active: activeTab === 'general' }"
                @click.prevent="activeTab = 'general'"
              >
                Dados gerais
              </a>
            </li>
            <li class="tab-list-item">
              <a
                href="#"
                :class="{ active: activeTab === 'permissions' }"
                @click.prevent="activeTab = 'permissions'"
              >
                Permissões
              </a>
            </li>
          </ul>

          <div class="tab-content">
            <div v-show="activeTab === 'general'" class="tab-item columns contact-modal-tab-panel contact-modal-general-panel">
              <div class="span-12">
                <Input
                  name="name"
                  label="Nome Completo"
                  v-model="form.name"
                  :error="getFieldError('name')"
                  :disabled="emailAlreadyExists"
                  @input="clearFieldError('name')"
                  required
                />
              </div>

              <div class="span-12">
                <Input
                  name="email1"
                  type="email"
                  label="E-mail"
                  v-model="form.email1"
                  :error="getFieldError('email1')"
                  @input="onEmailInput"
                  required
                />
                <p v-if="emailAlreadyExists" class="email-link-hint">
                  Este e-mail já está cadastrado. Ao confirmar, o contato será vinculado à empresa selecionada.
                </p>
              </div>

              <div
                class="span-12 relative contact-company-field"
                :class="{ 'has-field-error': fieldErrors.companyId }"
              >
                <label for="companyId" class="flex ai-c jc-sb">Empresa</label>
                <Select
                  :value="form.companyId"
                  :options="companies"
                  classSelectList="select-list-modal"
                  :useIcon="false"
                  @input="onCompanyChange"
                />
                <div v-if="fieldErrors.companyId" class="field-error">
                  {{ fieldErrors.companyId }}
                </div>
              </div>

              <div class="span-12 contact-portal-admin-field">
                <label class="portal-admin-option">
                  <input
                    type="checkbox"
                    name="portalAdmin"
                    v-model="form.portalAdmin"
                    @change="onPortalAdminChange"
                  />
                  Administrador do Portal do Cliente
                </label>
              </div>

              <template v-if="!isEdit && !emailAlreadyExists">
                <div class="span-12 contact-access-section">
                  <div class="generate-password-field">
                    <label class="generate-password-option">
                      <input
                        type="checkbox"
                        name="generatePassword"
                        v-model="form.generatePassword"
                        @change="onGeneratePasswordChange"
                      />
                      Gerar uma senha automática e notificar o contato por email
                    </label>
                  </div>

                  <template v-if="!form.generatePassword">
                    <div class="span-12">
                      <Input
                        name="password"
                        type="password"
                        label="Senha"
                        placeholder="Senha"
                        className="text-bold"
                        autocomplete="new-password"
                        :showPasswordToggle="true"
                        v-model="form.password"
                        :error="getFieldError('password')"
                        @input="onPasswordInput"
                        required
                      />
                    </div>

                    <div class="span-12">
                      <ul class="pass-validation">
                        <li class="flex ai-c gap1">
                          <span
                            class="indicator"
                            :class="{
                              success: passValidation.length,
                              error: !passValidation.length,
                            }"
                          ></span>
                          No mínimo 6 caracteres
                        </li>
                        <li class="flex ai-c gap1">
                          <span
                            class="indicator"
                            :class="{
                              success: passValidation.uppercase,
                              error: !passValidation.uppercase,
                            }"
                          ></span>
                          Pelo menos 1 letra maiúscula
                        </li>
                        <li class="flex ai-c gap1">
                          <span
                            class="indicator"
                            :class="{
                              success: passValidation.lowercase,
                              error: !passValidation.lowercase,
                            }"
                          ></span>
                          Pelo menos 1 letra minúscula
                        </li>
                        <li class="flex ai-c gap1">
                          <span
                            class="indicator"
                            :class="{
                              success: passValidation.number,
                              error: !passValidation.number,
                            }"
                          ></span>
                          Pelo menos 1 número
                        </li>
                        <li class="flex ai-c gap1">
                          <span
                            class="indicator"
                            :class="{
                              success: passValidation.special,
                              error: !passValidation.special,
                            }"
                          ></span>
                          Pelo menos 1 caracter especial !@#$%^&*()
                        </li>
                      </ul>
                    </div>

                    <div class="span-12">
                      <Input
                        name="passwordRepeat"
                        type="password"
                        label="Repita a Senha"
                        placeholder="Repita a Senha"
                        className="text-bold"
                        autocomplete="new-password"
                        :showPasswordToggle="true"
                        v-model="form.passwordRepeat"
                        :error="getFieldError('passwordRepeat')"
                        @input="clearFieldError('passwordRepeat')"
                        required
                      />
                    </div>
                  </template>
                </div>
              </template>
            </div>

            <div v-show="activeTab === 'permissions'" class="tab-item columns contact-modal-tab-panel">
              <div
                class="span-12 contact-permissions-section"
                :class="{ 'has-field-error': fieldErrors.permissions }"
              >
                <p class="contact-permissions-hint">
                  Clique nos itens para mover entre as listas.
                </p>
                <DualListSelect
                  v-model="form.permissions"
                  :options="menuPermissionOptions"
                  :compact="true"
                  available-title="Permissões disponíveis"
                  selected-title="Permissões selecionadas"
                  @input="clearFieldError('permissions')"
                />
                <div v-if="fieldErrors.permissions" class="field-error">
                  {{ fieldErrors.permissions }}
                </div>
              </div>

              <div v-if="showReportFieldsSelect" class="span-12 contact-permissions-section">
                <div class="contact-permissions-section-header">
                  <h4 class="contact-permissions-section-title">Campos visíveis no relatório</h4>
                  <div class="contact-permissions-section-divider"></div>
                </div>
                <p class="contact-permissions-hint">
                  Selecione quais colunas do relatório este contato poderá visualizar.
                </p>
                <DualListSelect
                  v-model="form.reportFields"
                  :options="reportFieldOptions"
                  :compact="true"
                  available-title="Campos disponíveis"
                  selected-title="Campos selecionados"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </template>

    <template #cancel-button>
      <button
        type="button"
        class="btn solid light medium"
        :disabled="isSaving"
        @click="$emit('request-close')"
      >
        Cancelar
      </button>
    </template>

    <template #confirm-button>
      <button
        type="button"
        class="btn solid primary medium"
        :disabled="isSaving"
        @click="submit"
      >
        {{ isSaving ? 'Salvando...' : confirmButtonLabel }}
      </button>
    </template>
  </Modal>
</template>

<script>
import { mapActions } from 'vuex';

import api from '@/services/api';
import Modal from '@/components/Modal.vue';
import Input from '@/components/DataInput/Input.vue';
import Select from '@/components/DataInput/Select.vue';
import DualListSelect from '@/components/DataInput/DualListSelect.vue';
const PASSWORD_PATTERN = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()]).{6,}$/;

const DEFAULT_PASS_VALIDATION = {
  length: false,
  lowercase: false,
  uppercase: false,
  number: false,
  special: false,
};

const DEFAULT_FIELD_ERRORS = {
  name: '',
  email1: '',
  companyId: '',
  password: '',
  passwordRepeat: '',
  permissions: '',
};

export default {
  name: 'ContactModal',
  components: {
    Modal,
    Input,
    Select,
    DualListSelect,
  },
  props: {
    handler: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    contact: {
      type: Object,
      default: null,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    menuPermissionOptions: {
      type: Array,
      default: () => [],
    },
    reportFieldOptions: {
      type: Array,
      default: () => [],
    },
    reportsPermissionId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 'general',
      form: {
        name: '',
        email1: '',
        companyId: null,
        password: '',
        passwordRepeat: '',
        generatePassword: true,
        previousCompanyId: null,
        portalAdmin: false,
        permissions: [],
        reportFields: [],
      },
      passValidation: { ...DEFAULT_PASS_VALIDATION },
      fieldErrors: { ...DEFAULT_FIELD_ERRORS },
      emailAlreadyExists: false,
      isSaving: false,
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    confirmButtonLabel() {
      if (this.isEdit) {
        return 'Salvar';
      }
      if (this.emailAlreadyExists) {
        return 'Vincular';
      }
      return 'Adicionar';
    },
    showReportFieldsSelect() {
      return this.reportsPermissionId !== null
        && this.form.permissions.includes(this.reportsPermissionId);
    },
  },
  watch: {
    handler(open) {
      if (open) {
        this.resetForm();
      }
    },
    'form.permissions'(permissions) {
      if (this.reportsPermissionId === null || !permissions.includes(this.reportsPermissionId)) {
        this.form.reportFields = [];
      }
    },
  },
  methods: {
    ...mapActions(['toggleLoading']),
    getFieldError(field) {
      return new Error(this.fieldErrors[field] || '');
    },
    resetFieldErrors() {
      this.fieldErrors = { ...DEFAULT_FIELD_ERRORS };
    },
    clearFieldError(field) {
      if (this.fieldErrors[field]) {
        this.fieldErrors[field] = '';
      }
    },
    onCompanyChange(value) {
      this.form.companyId = value;
      this.clearFieldError('companyId');
    },
    onEmailInput() {
      this.emailAlreadyExists = false;
      this.clearFieldError('email1');
    },
    onPasswordInput(value) {
      this.validatePassWhileWrite(value);
      this.clearFieldError('password');
    },
    onGeneratePasswordChange() {
      this.form.password = '';
      this.form.passwordRepeat = '';
      this.resetPassValidation();
      this.clearFieldError('password');
      this.clearFieldError('passwordRepeat');
    },
    onPortalAdminChange() {
      if (this.form.portalAdmin) {
        this.clearFieldError('permissions');
      }
    },
    resetPassValidation() {
      this.passValidation = { ...DEFAULT_PASS_VALIDATION };
    },
    validatePassWhileWrite(value) {
      const validators = {
        length: new RegExp('^.{6,}$'),
        lowercase: new RegExp('[a-z]'),
        uppercase: new RegExp('[A-Z]'),
        number: new RegExp('(?=.*\\d)'),
        special: new RegExp('(?=.*[!@#$%^&*()])'),
      };

      this.passValidation = {
        length: validators.length.test(value),
        lowercase: validators.lowercase.test(value),
        uppercase: validators.uppercase.test(value),
        number: validators.number.test(value),
        special: validators.special.test(value),
      };
    },
    resetForm() {
      this.activeTab = 'general';
      this.isSaving = false;
      this.resetPassValidation();
      this.resetFieldErrors();
      this.emailAlreadyExists = false;

      if (this.isEdit && this.contact) {
        this.form = {
          name: this.contact.name || '',
          email1: this.contact.email1 || '',
          companyId: this.contact.companyId || null,
          password: '',
          passwordRepeat: '',
          generatePassword: true,
          previousCompanyId: this.contact.companyId || null,
          portalAdmin: Boolean(this.contact.portalAdmin),
          permissions: this.contact.permissions ? [...this.contact.permissions] : [],
          reportFields: this.contact.reportFields ? [...this.contact.reportFields] : [],
        };
      } else {
        this.form = {
          name: '',
          email1: '',
          companyId: this.companies.length === 1 ? this.companies[0].value : null,
          password: '',
          passwordRepeat: '',
          generatePassword: true,
          previousCompanyId: null,
          portalAdmin: false,
          permissions: [],
          reportFields: [],
        };
      }
    },
    validateForm() {
      this.resetFieldErrors();
      let isValid = true;

      if (!this.form.name || !this.form.name.trim()) {
        this.fieldErrors.name = 'Informe o nome do contato.';
        isValid = false;
      }

      if (!this.form.email1 || !this.form.email1.trim()) {
        this.fieldErrors.email1 = 'Informe o e-mail do contato.';
        isValid = false;
      }

      if (!this.form.companyId) {
        this.fieldErrors.companyId = 'Selecione a empresa.';
        isValid = false;
      }

      if (!this.isEdit && !this.emailAlreadyExists && !this.form.generatePassword) {
        if (!this.form.password || !this.form.password.trim()) {
          this.fieldErrors.password = 'Informe a senha do contato.';
          isValid = false;
        } else if (!PASSWORD_PATTERN.test(this.form.password)) {
          this.fieldErrors.password = 'A senha deve conter no mínimo 6 caracteres, incluindo letras minúsculas e maiúsculas, números e caracteres especiais como !@#$%^&*().';
          isValid = false;
        }

        if (!this.form.passwordRepeat || !this.form.passwordRepeat.trim()) {
          this.fieldErrors.passwordRepeat = 'Repita a senha do contato.';
          isValid = false;
        } else if (this.form.password !== this.form.passwordRepeat) {
          this.fieldErrors.passwordRepeat = 'A senha e a confirmação devem ser iguais.';
          isValid = false;
        }
      }

      if (!this.isEdit && !this.form.portalAdmin && this.form.permissions.length === 0) {
        this.fieldErrors.permissions = 'Selecione ao menos uma permissão.';
        isValid = false;
      }

      return isValid;
    },
    focusTabWithErrors() {
      const generalFields = ['name', 'email1', 'companyId', 'password', 'passwordRepeat'];
      const hasGeneralErrors = generalFields.some((field) => this.fieldErrors[field]);

      if (hasGeneralErrors) {
        this.activeTab = 'general';
        return;
      }

      if (this.fieldErrors.permissions) {
        this.activeTab = 'permissions';
      }
    },
    buildPermissionsPayload() {
      const permissionIds = [...this.form.permissions];

      if (this.showReportFieldsSelect) {
        permissionIds.push(...this.form.reportFields);
      }

      return {
        portalAdmin: this.form.portalAdmin,
        permissions: [...new Set(permissionIds)],
      };
    },
    async submit() {
      if (this.isSaving) {
        return;
      }

      if (!this.validateForm()) {
        this.focusTabWithErrors();
        return;
      }

      this.isSaving = true;
      this.toggleLoading(true);

      try {
        if (this.isEdit) {
          await api.put(`/customers/contacts/${this.contact.id}`, {
            name: this.form.name.trim(),
            email1: this.form.email1.trim().toLowerCase(),
            companyId: this.form.companyId,
            previousCompanyId: this.form.previousCompanyId,
            ...this.buildPermissionsPayload(),
          });

          this.$toast.success('Contato atualizado com sucesso!', { position: 'top-center' });
        } else {
          const payload = {
            name: this.form.name.trim(),
            email1: this.form.email1.trim().toLowerCase(),
            companyId: this.form.companyId,
            ...this.buildPermissionsPayload(),
          };

          if (this.emailAlreadyExists) {
            payload.forceLink = true;
          } else {
            payload.generatePassword = this.form.generatePassword;

            if (!this.form.generatePassword) {
              payload.password = this.form.password;
            }
          }

          await api.post('/customers/contacts', payload);

          const successMessage = this.emailAlreadyExists
            ? 'Contato vinculado com sucesso!'
            : this.form.generatePassword
              ? 'Contato criado com sucesso! Os dados de acesso foram enviados por e-mail.'
              : 'Contato criado com sucesso!';

          this.$toast.success(successMessage, { position: 'top-center' });
        }

        this.$emit('saved');
        this.$emit('request-close');
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível salvar o contato.';

        if (this.isEdit) {
          if (message.includes('já está em uso')) {
            this.activeTab = 'general';
            this.fieldErrors.email1 = message;
            return;
          }

          this.$swal.fire({
            title: 'Editar Contato',
            text: message,
            icon: 'error',
          });
          return;
        }

        if (message.includes('já está em uso') && !this.emailAlreadyExists) {
          this.activeTab = 'general';
          this.emailAlreadyExists = true;
          return;
        }

        if (message.includes('já está vinculado')) {
          this.activeTab = 'general';
          this.fieldErrors.companyId = message;
          return;
        }

        this.$swal.fire({
          title: this.emailAlreadyExists ? 'Vincular Contato' : 'Adicionar Contato',
          text: message,
          icon: 'error',
        });
      } finally {
        this.isSaving = false;
        this.toggleLoading(false);
      }
    },
  },
};
</script>

<style scoped>
.contact-modal-form {
  gap: 0.75rem;
}

.contact-modal-tabs .tab-list {
  margin: 0 0 0.75rem;
  border-bottom: 1px solid var(--input-border-color, rgba(0, 0, 0, 0.15));
}

.contact-modal-tabs .tab-list-item a {
  padding: 0.5rem 1rem !important;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.contact-modal-tabs .tab-list-item a.active,
.contact-modal-tabs .tab-list-item a:hover {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
  background-color: transparent;
}

.contact-modal-tabs .tab-list-item a.disabled {
  color: rgba(0, 0, 0, 0.3);
  cursor: not-allowed;
  pointer-events: none;
}

.contact-portal-admin-field {
  margin-top: 0;
}

.portal-admin-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.portal-admin-option input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.contact-modal-tab-panel {
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.contact-modal-general-panel >>> label {
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.contact-modal-general-panel >>> input[type="text"],
.contact-modal-general-panel >>> input[type="email"],
.contact-modal-general-panel >>> input[type="password"],
.contact-modal-general-panel >>> input[type="search"] {
  height: 2.75rem;
  padding: 0 0.75rem;
  font-size: 0.9rem;
}

.contact-modal-general-panel >>> input[type="checkbox"] {
  width: 0.9rem;
  height: 0.9rem;
}

.contact-modal-general-panel >>> .with-password-toggle input {
  padding-right: 2.25rem;
}

.contact-modal-general-panel >>> .password-toggle {
  right: 0.5rem;
}

.contact-modal-general-panel >>> .password-toggle svg {
  width: 16px;
  height: 16px;
}

.contact-modal-form >>> .field-error {
  margin-top: 0.25rem;
}

.email-link-hint {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #c0392b;
}

.pass-validation {
  padding: 0;
  margin: 0;
  list-style: none;
  font-size: 0.85rem;
}

.pass-validation .indicator {
  width: 10px;
  height: 10px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  flex-shrink: 0;
}

.pass-validation .indicator.success {
  background-color: #27ae60;
}

.pass-validation .indicator.error {
  background-color: #c0392b;
}

.contact-access-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0;
}

.contact-access-section-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-access-section-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--black-color, #000);
}

.contact-access-section-divider {
  width: 100%;
  height: 1px;
  background-color: var(--input-border-color, rgba(0, 0, 0, 0.15));
}

.generate-password-field {
  display: flex;
  flex-direction: column;
}

.generate-password-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.generate-password-option input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.generate-password-hint {
  /* margin: 0 0 0 1.5rem; */
  font-size: 0.95rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.65);
}

.contact-permissions-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-permissions-section-header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.contact-permissions-section-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--black-color, #000);
}

.contact-permissions-section-divider {
  width: 100%;
  height: 1px;
  background-color: var(--input-border-color, rgba(0, 0, 0, 0.15));
}

.contact-permissions-hint {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.6);
}

.contact-company-field >>> .select-list-modal {
  height: 2.75rem;
  display: flex;
  align-items: center;
  padding: 0 2.5rem 0 0.75rem;
  border: var(--input-border) !important;
  border-radius: var(--medium-border-radius) !important;
}

.contact-company-field >>> .select-list-modal .title {
  display: none;
}

.contact-company-field >>> .select-list-modal .selected-option {
  width: 100%;
  height: auto;
  line-height: 1.2;
  font-size: 0.9rem;
  margin: 0;
}

.contact-company-field.has-field-error >>> .select-list-modal {
  border: var(--input-border-error) !important;
}
</style>
